












































import { Vue, Component } from 'vue-property-decorator';

import ReferralService from '@/api/ReferralService';

import { Typography } from '@/components/atoms';
import { ContentContainer, FilledButton } from '@/components/molecules';
import EmptyState from '@/components/organisms/Referral/EmptyState.vue';
import ReferralItem from '@/components/organisms/Referral/ReferralItem.vue';
import PerformanceList from '@/components/molecules/PerformanceList/PerformanceList.vue';

import {
  IBusinessReferral,
  IBusinessReferralChart
} from '@/models/marketingCenter/referral';

import { treatError } from '@/utils';
import eventBus from '@/utils/eventBus';
import isMobile from '@/utils/mobile-check';
import flagKeys from '@/constants/flagKeys';
import { formatMoney } from '@/utils/moneyFormarter';

@Component({
  components: {
    Typography,
    ContentContainer,
    FilledButton,
    EmptyState,
    ReferralItem,
    PerformanceList
  }
})
export default class ReferralResume extends Vue {
  referralService = new ReferralService();
  loading: boolean = false;
  campaign: IBusinessReferral = {};
  chart: IBusinessReferralChart = {};

  get totalValues() {
    return {
      coinsRewarded: {
        counter: this.campaign.coinsRewarded,
        label: 'Total Coins Rewarded through Referrals',
        description:
          'Total coins given to members for successful referrals since the beginning of your participation through today.'
      },
      referralCoverted: {
        counter: this.campaign.joinedMembers,
        label: 'Total Referrals Converted',
        description:
          'Total number of referrals that turned into new sign-ups since the beginning of your participation through today, showing the success of the referral efforts.'
      },
      conversionRate: {
        counter: this.campaign.conversion,
        sufixCounter: '%',
        label: 'Referral Conversion Rate',
        description:
          'The percentage of people who made a purchase after joining through a referral link, based on all campaigns so far.'
      },
      revenue: {
        counter: this.campaign.revenue,
        formattedCounter: formatMoney(this.country, this.campaign.revenue),
        label: 'Revenue from Referrals',
        description:
          'Total revenue generated from members who joined through referrals and made purchases, showing the impact of referrals since the beginning of your participation through today.'
      }
    };
  }

  get country() {
    return this.$store.getters['internationalization/getCountrySettings'];
  }

  get isMobileDevice() {
    return isMobile();
  }

  get currencySymbol() {
    return this.$store.getters['internationalization/getCountrySettings']
      .currencySymbol;
  }

  get tPrefix() {
    return 'referralProgram';
  }

  get titlePage() {
    if (this.emptyState && this.isMobileDevice) return '';

    return this.$t(`${this.tPrefix}.title`);
  }

  get isBillingModuleV4() {
    return this.flags.includes(flagKeys.billingModuleV4);
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get emptyState() {
    return !this.loading && !this.campaign.id;
  }

  get disabledEmptyState() {
    if (this.flags.includes(flagKeys.billingModuleV4)) return false;
    return !this.$store.getters[
      'merchant/getConnectedProviderWithWidgetSettings'
    ];
  }

  async getChart() {
    if (!this.campaign.id) return;

    const { data } = await this.referralService.getChart(this.campaign.id);
    this.chart = data;
  }

  async getReferralProgram() {
    try {
      this.loading = true;
      this.$showLoaderWithLocker();

      this.referralService.disableInterception();
      const { data } = await this.referralService.getReferral();

      this.campaign = data;

      await this.getChart();
    } catch (e) {
      // just to not add this intentional error to datadog
    } finally {
      this.loading = false;
      this.$hideLoader();
    }
  }

  async updateActiveCampaign({ id, status }: { id: string; status: boolean }) {
    try {
      this.$showLoaderWithLocker();

      const response = await this.referralService.changeStatus(id);

      if (response.status !== 200) return;

      this.campaign = {
        ...this.campaign,
        isActive: status
      };

      const statusLabel = status ? 'activated' : 'deactivated';

      this.$notification(
        String(this.$t(`${this.tPrefix}.edit.feedback.${statusLabel}`))
      );

      this.$analytics({
        eventCategory: 'Referral Program | Active/Inactive campaign',
        eventAction: 'click',
        eventLabel: `Campaign successfully ${statusLabel}`
      });
    } catch (e) {
      treatError(e);
    } finally {
      this.$hideLoader();
    }
  }

  mounted() {
    eventBus.$on('changeReferralCampaignStatus', this.updateActiveCampaign);
  }

  beforeDestroy() {
    eventBus.$off('changeReferralCampaignStatus', this.updateActiveCampaign);
  }

  async created() {
    await this.getReferralProgram();
  }
}
